import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth } from "../../context/AuthContext.js";
import { useTenantFeatureFlags, useTenantBetFlags } from "../../lib/tenant-feature-flags.js";
import { allNavLinks } from "../../nav/primary-nav.js";
import {
  SETTINGS_TAB_IDS,
  SETTINGS_TAB_LABELS,
} from "../settings/settings-tab-id.js";
import { buildConfigurationCards } from "../../lib/configuration-cards.js";
import { fetchDashboardIdentities, fetchDashboardSessions } from "../../lib/api.js";
import { getTenantExternalId } from "../../lib/tenant-settings.js";
import { identityLabel } from "../../lib/identity-label.js";

// ── Command Palette (Cmd/Ctrl-K) ────────────────────────────────────────────
// Extracted from TopBar so the global header (ContentHeader) and standalone
// TopBar usages share one implementation.

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [identityHits, setIdentityHits] = useState<
    Array<{ id: string; label: string; sub: string; to: string }>
  >([]);
  const [sessionHits, setSessionHits] = useState<
    Array<{ id: string; label: string; sub: string; to: string }>
  >([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { user } = useAuth();
  const { loading: flagsLoading, flags } = useTenantFeatureFlags({
    role: user?.role ?? null,
    tenantExternalId: user?.tenantExternalId ?? null,
  });
  const { loading: betFlagsLoading, flags: betFlags } = useTenantBetFlags({
    role: user?.role ?? null,
    tenantExternalId: user?.tenantExternalId ?? null,
  });
  // Same gating rules as SecondaryNav: hide feature/bet-gated links while
  // flags load or when the tenant doesn't have them.
  const navItems: Array<{ sectionId: string; id: string; label: string; to: string }> =
    allNavLinks().filter((link) => {
      if (link.featureKey) {
        if (flagsLoading) return false;
        if (flags[link.featureKey] !== true) return false;
      }
      if (link.betKey) {
        if (betFlagsLoading) return false;
        if (betFlags[link.betKey] !== true) return false;
      }
      return true;
    }).map((link) => ({
      sectionId: link.sectionId,
      id: link.id,
      // Prefix with section label so results are scannable ("Platform → IdP sync")
      label: `${link.sectionId.charAt(0).toUpperCase()}${link.sectionId.slice(1)} → ${link.label}`,
      to: link.to,
    }));
  // RFC-004 R5.4 — settings search: index every Settings tab and every
  // platform-configuration destination as a "Settings → …" deep link, gated by
  // the same role rules the settings UI itself uses.
  const cardRole =
    user?.role === "platform_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "tenant_operator"
      ? user.role
      : "tenant_admin";
  // Deduplicate: if a settings card points at the same URL as a nav link,
  // the nav link already covers it — skip the settings card entry.
  const navUrls = new Set(navItems.map((n) => n.to));
  const settingsItems = [
    ...SETTINGS_TAB_IDS.filter(
      (id) => id !== "features" || user?.role === "tenant_admin"
    ).map((id) => ({
      sectionId: "settings-tabs",
      id,
      label: `Settings → ${SETTINGS_TAB_LABELS[id]}`,
      to: `/settings?tab=${id}`,
    })),
    ...buildConfigurationCards({ role: cardRole })
      .filter((c) => c.to !== "/settings")
      .filter((c) => !navUrls.has(c.to))
      .map((c) => ({
        sectionId: "settings-cards",
        id: c.to,
        label: `Settings → ${c.title}`,
        to: c.to,
      })),
  ];
  const allItems = [...navItems, ...settingsItems];
  const filtered = query.trim()
    ? allItems.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase())
      )
    : allItems;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = query.trim();
    const delayMs = q.length < 2 || !getTenantExternalId() ? 0 : 300;
    const t = window.setTimeout(() => {
      if (q.length < 2 || !getTenantExternalId()) {
        setIdentityHits([]);
        setSessionHits([]);
        setEntityLoading(false);
        return;
      }
      setEntityLoading(true);
      void Promise.all([
        fetchDashboardIdentities({ search: q, limit: 8, offset: 0 }),
        fetchDashboardSessions({ search: q, limit: 8, offset: 0 }),
      ])
        .then(([idRes, sessRes]) => {
          setIdentityHits(
            idRes.items.map((i) => ({
              id: `id-${i.externalId}`,
              label: i.displayName || i.identifier,
              sub: i.externalId,
              to: `/identities/${encodeURIComponent(i.externalId)}`,
            }))
          );
          setSessionHits(
            sessRes.items.map((s, idx) => ({
              id: `sess-${s.sessionId || idx}`,
              label:
                s.sessionId.length > 14 ? `${s.sessionId.slice(0, 14)}…` : s.sessionId || `session-${idx}`,
              sub: identityLabel(s),
              to: `/sessions?search=${encodeURIComponent(q)}`,
            }))
          );
        })
        .catch(() => {
          setIdentityHits([]);
          setSessionHits([]);
        })
        .finally(() => setEntityLoading(false));
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [query]);

  const go = useCallback(
    (to: string) => {
      navigate(to);
      onClose();
    },
    [navigate, onClose]
  );

  // Flat, ordered list of every navigable result (pages, then identities,
  // then sessions) so ArrowUp/ArrowDown/Enter work across all groups.
  const results: Array<{ to: string }> = [
    ...filtered.map((item) => ({ to: item.to })),
    ...identityHits,
    ...sessionHits,
  ];

  // Reset the highlight whenever the result set changes.
  useEffect(() => {
    setHighlighted(0);
  }, [query, identityHits.length, sessionHits.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (results.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((i) => (i + 1) % results.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((i) => (i - 1 + results.length) % results.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = results[Math.min(highlighted, results.length - 1)];
        if (target) go(target.to);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  // Keep the highlighted row visible while arrowing through a long list.
  const highlightedRef = useCallback((el: HTMLButtonElement | null) => {
    el?.scrollIntoView({ block: "nearest" });
  }, []);

  const showFullEmpty =
    filtered.length === 0 &&
    (query.trim().length < 2 ||
      (!entityLoading && identityHits.length === 0 && sessionHits.length === 0));

  return (
    <div className="command-overlay" onClick={onClose}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-card px-4 py-3">
          <Search className="h-4 w-4 text-subtle flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, identities, sessions..."
            className="flex-1 bg-transparent text-sm text-app placeholder:text-subtle outline-none"
          />
          <kbd className="hidden sm:flex items-center gap-0.5 rounded border border-card px-1.5 py-0.5 text-[10px] font-mono text-subtle">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[320px] overflow-y-auto py-2 px-2">
          {showFullEmpty ? (
            <p className="px-4 py-8 text-center text-sm text-subtle">
              No results found
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.length > 0 ? (
                <div className="space-y-0.5">
                  <p className="px-3 pt-1 text-[10px] font-semibold uppercase tracking-wide text-subtle">
                    Pages
                  </p>
                  {filtered.map((item, i) => (
                    <button
                      key={`${item.sectionId}-${item.id}`}
                      ref={highlighted === i ? highlightedRef : undefined}
                      type="button"
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors duration-150 ${
                        highlighted === i
                          ? "bg-app-hover text-app"
                          : "text-muted hover:bg-app-hover hover:text-app"
                      }`}
                      onClick={() => go(item.to)}
                      onMouseMove={() => setHighlighted(i)}
                    >
                      <span className="flex-1">{item.label}</span>
                      <span className="text-xs text-subtle font-mono">{item.to}</span>
                    </button>
                  ))}
                </div>
              ) : null}

              {query.trim().length >= 2 && getTenantExternalId() ? (
                <div className="space-y-0.5 border-t border-card pt-2">
                  <p className="px-3 text-[10px] font-semibold uppercase tracking-wide text-subtle">
                    Identities &amp; sessions
                  </p>
                  {entityLoading ? (
                    <p className="px-4 py-3 text-xs text-subtle">Searching…</p>
                  ) : (
                    <>
                      {identityHits.map((h, i) => (
                        <button
                          key={h.id}
                          ref={highlighted === filtered.length + i ? highlightedRef : undefined}
                          type="button"
                          className={`flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors duration-150 ${
                            highlighted === filtered.length + i
                              ? "bg-app-hover text-app"
                              : "text-muted hover:bg-app-hover hover:text-app"
                          }`}
                          onClick={() => go(h.to)}
                          onMouseMove={() => setHighlighted(filtered.length + i)}
                        >
                          <span className="flex items-center gap-2">
                            <span className="rounded bg-app-hover px-1.5 py-0.5 text-[9px] font-semibold uppercase text-muted">
                              Identity
                            </span>
                            <span className="flex-1">{h.label}</span>
                          </span>
                          <span className="pl-[3.25rem] text-[11px] font-mono text-subtle">
                            {h.sub}
                          </span>
                        </button>
                      ))}
                      {sessionHits.map((h, i) => (
                        <button
                          key={h.id}
                          ref={
                            highlighted === filtered.length + identityHits.length + i
                              ? highlightedRef
                              : undefined
                          }
                          type="button"
                          className={`flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors duration-150 ${
                            highlighted === filtered.length + identityHits.length + i
                              ? "bg-app-hover text-app"
                              : "text-muted hover:bg-app-hover hover:text-app"
                          }`}
                          onClick={() => go(h.to)}
                          onMouseMove={() => setHighlighted(filtered.length + identityHits.length + i)}
                        >
                          <span className="flex items-center gap-2">
                            <span className="rounded bg-app-hover px-1.5 py-0.5 text-[9px] font-semibold uppercase text-muted">
                              Session
                            </span>
                            <span className="flex-1 font-mono text-xs">{h.label}</span>
                          </span>
                          <span className="pl-[3.25rem] text-[11px] text-subtle">
                            {h.sub}
                          </span>
                        </button>
                      ))}
                      {identityHits.length === 0 && sessionHits.length === 0 ? (
                        <p className="px-4 py-2 text-xs text-subtle">
                          No identity or session matches.
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-4 border-t border-card px-4 py-2.5 text-[10px] text-subtle">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-card px-1 font-mono">&uarr;&darr;</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-card px-1 font-mono">&crarr;</kbd>
            Open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-card px-1 font-mono">Esc</kbd>
            Close
          </span>
        </div>
      </div>
    </div>
  );
}
